import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminBlogPost,
  deleteAdminBlogImage,
  deleteAdminBlogPost,
  getAdminBlogPosts,
  updateAdminBlogPost,
  uploadAdminBlogImage,
} from "../../../api/blogAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";

const LANGS = ["en", "ar", "ku"];

const SECTION_TYPES = [
  { value: "paragraph", label: "Paragraph" },
  { value: "heading", label: "Heading" },
];

const emptyTranslation = () => ({ title: "", text: "", caption: "", sections: [] });

const newSectionKey = () => `sec-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const normalizeSectionRow = (raw, idx) => {
  const bulletsRaw = raw?.bullets ?? raw?.Bullets;
  const bullets = Array.isArray(bulletsRaw)
    ? bulletsRaw
        .map((x) => (typeof x === "string" ? x : String(x?.text ?? x ?? "")).trim())
        .filter(Boolean)
    : [];
  const idRaw = raw?.id ?? raw?.Id;
  return {
    _key: idRaw != null ? `id-${idRaw}-${idx}` : newSectionKey(),
    id: idRaw != null && idRaw !== "" ? idRaw : null,
    displayOrder: Number(raw?.displayOrder ?? raw?.DisplayOrder ?? idx + 1) || idx + 1,
    sectionType: String(raw?.sectionType ?? raw?.SectionType ?? "paragraph")
      .toLowerCase()
      .trim() || "paragraph",
    title: String(raw?.title ?? raw?.Title ?? "").trim(),
    body: String(raw?.body ?? raw?.Body ?? "").trim(),
    bullets,
  };
};

const buildEmptyForm = () => ({
  publishedAtUtc: new Date().toISOString(),
  imageUrl: "",
  isActive: true,
  translations: {
    en: emptyTranslation(),
    ar: emptyTranslation(),
    ku: emptyTranslation(),
  },
});

const toInputDateTime = (isoValue) => {
  if (!isoValue) return "";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const hh = pad(date.getHours());
  const mm = pad(date.getMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
};

const toUtcIso = (inputValue) => {
  if (!inputValue) return new Date().toISOString();
  const date = new Date(inputValue);
  if (Number.isNaN(date.getTime())) return new Date().toISOString();
  return date.toISOString();
};

const normalizeTranslations = (translations) => {
  const byLang = { en: emptyTranslation(), ar: emptyTranslation(), ku: emptyTranslation() };
  const source = Array.isArray(translations)
    ? translations
    : Object.entries(translations || {}).map(([language, value]) => ({ language, ...(value || {}) }));
  source.forEach((tr) => {
    const lang = String(tr?.language || "").toLowerCase();
    if (!LANGS.includes(lang)) return;
    const secSource = tr?.sections ?? tr?.Sections;
    const sections = Array.isArray(secSource) ? secSource.map((row, i) => normalizeSectionRow(row, i)) : [];
    byLang[lang] = {
      title: tr?.title || tr?.Title || "",
      text: tr?.text || tr?.Text || "",
      caption: tr?.caption || tr?.Caption || "",
      sections,
    };
  });
  return byLang;
};

/** When API returns a flat post or root `sections` alongside `translations`. */
const mergeRootPostIntoTranslations = (post, byLang) => {
  const out = { ...byLang };
  const rootSections = Array.isArray(post?.sections ?? post?.Sections)
    ? post.sections ?? post.Sections
    : [];
  const hasTranslationsArray = Array.isArray(post?.translations) && post.translations.length > 0;

  if (!hasTranslationsArray) {
    out.en = {
      title: post?.title || post?.Title || "",
      text: post?.text || post?.Text || "",
      caption: post?.caption || post?.Caption || "",
      sections: rootSections.map((row, i) => normalizeSectionRow(row, i)),
    };
    return out;
  }

  if (rootSections.length && (!out.en.sections || out.en.sections.length === 0)) {
    out.en = {
      ...out.en,
      title: out.en.title || post?.title || "",
      text: out.en.text || post?.text || "",
      caption: out.en.caption || post?.caption || "",
      sections: rootSections.map((row, i) => normalizeSectionRow(row, i)),
    };
  }
  return out;
};

const toPayloadTranslations = (map) =>
  LANGS.map((language) => {
    const tr = map[language] || emptyTranslation();
    const sections = (tr.sections || []).map((s, i) => {
      const st = s.sectionType === "heading" ? "heading" : "paragraph";
      const idNum = Number(s.id);
      const hasId = s.id != null && s.id !== "" && Number.isFinite(idNum) && idNum > 0;
      return {
        ...(hasId ? { id: idNum } : {}),
        displayOrder: i + 1,
        sectionType: st,
        title: st === "heading" ? (String(s.title || "").trim() || null) : null,
        body: String(s.body || "").trim() || null,
        bullets: Array.isArray(s.bullets) ? s.bullets.map((b) => String(b || "").trim()).filter(Boolean) : [],
      };
    });
    return {
      language,
      title: tr.title || "",
      text: tr.text || "",
      caption: tr.caption || "",
      sections,
    };
  });

const BlogManager = () => {
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posts, setPosts] = useState([]);
  const [editingPostId, setEditingPostId] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [form, setForm] = useState(buildEmptyForm);
  const [imageFile, setImageFile] = useState(null);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await getAdminBlogPosts();
      const list = Array.isArray(data) ? data : [];
      setPosts(list);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load blog posts",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const sortedPosts = useMemo(
    () =>
      [...posts].sort((a, b) => {
        const ad = new Date(a?.publishedAtUtc || 0).getTime();
        const bd = new Date(b?.publishedAtUtc || 0).getTime();
        return bd - ad;
      }),
    [posts]
  );

  const openCreate = () => {
    setIsEditorOpen(true);
    setEditingPostId(null);
    setForm(buildEmptyForm());
    setImageFile(null);
  };

  const openEdit = (post) => {
    setIsEditorOpen(true);
    setEditingPostId(post.id);
    const mergedTranslations = mergeRootPostIntoTranslations(post, normalizeTranslations(post?.translations));
    setForm({
      publishedAtUtc: post?.publishedAtUtc || new Date().toISOString(),
      imageUrl: post?.imageUrl || post?.ImageUrl || "",
      isActive: Boolean(post?.isActive),
      translations: mergedTranslations,
    });
    setImageFile(null);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingPostId(null);
    setForm(buildEmptyForm());
    setImageFile(null);
  };

  const addSection = (lang, sectionType) => {
    setForm((prev) => {
      const tr = prev.translations[lang] || emptyTranslation();
      const sections = [...(tr.sections || [])];
      sections.push({
        _key: newSectionKey(),
        id: null,
        displayOrder: sections.length + 1,
        sectionType,
        title: "",
        body: "",
        bullets: [],
      });
      return {
        ...prev,
        translations: { ...prev.translations, [lang]: { ...tr, sections } },
      };
    });
  };

  const updateSection = (lang, index, patch) => {
    setForm((prev) => {
      const tr = prev.translations[lang] || emptyTranslation();
      const sections = (tr.sections || []).map((s, i) => (i === index ? { ...s, ...patch } : s));
      return {
        ...prev,
        translations: { ...prev.translations, [lang]: { ...tr, sections } },
      };
    });
  };

  const removeSection = (lang, index) => {
    setForm((prev) => {
      const tr = prev.translations[lang] || emptyTranslation();
      const sections = (tr.sections || []).filter((_, i) => i !== index);
      return {
        ...prev,
        translations: { ...prev.translations, [lang]: { ...tr, sections } },
      };
    });
  };

  const moveSection = (lang, index, delta) => {
    setForm((prev) => {
      const tr = prev.translations[lang] || emptyTranslation();
      const sections = [...(tr.sections || [])];
      const j = index + delta;
      if (j < 0 || j >= sections.length) return prev;
      const next = [...sections];
      [next[index], next[j]] = [next[j], next[index]];
      return {
        ...prev,
        translations: { ...prev.translations, [lang]: { ...tr, sections: next } },
      };
    });
  };

  const savePost = async () => {
    setSaving(true);
    const payload = {
      publishedAtUtc: toUtcIso(form.publishedAtUtc),
      imageUrl: form.imageUrl || null,
      isActive: Boolean(form.isActive),
      translations: toPayloadTranslations(form.translations),
    };
    try {
      if (editingPostId) {
        let nextImageUrl = form.imageUrl || null;
        if (imageFile) {
          const uploaded = await uploadAdminBlogImage({
            id: editingPostId,
            language: "en",
            file: imageFile,
          });
          nextImageUrl =
            uploaded?.imageUrl ||
            (Array.isArray(uploaded?.translations)
              ? uploaded.translations.find((tr) => String(tr?.language).toLowerCase() === "en")?.imageUrl
              : nextImageUrl);
        }
        await updateAdminBlogPost({
          id: editingPostId,
          payload: {
            ...payload,
            imageUrl: nextImageUrl,
          },
        });
        notify({ type: "success", title: "Blog post updated" });
      } else {
        const created = await createAdminBlogPost(payload);
        const createdId = created?.id;
        if (createdId && imageFile) {
          const uploaded = await uploadAdminBlogImage({
            id: createdId,
            language: "en",
            file: imageFile,
          });
          const uploadedImageUrl =
            uploaded?.imageUrl ||
            (Array.isArray(uploaded?.translations)
              ? uploaded.translations.find((tr) => String(tr?.language).toLowerCase() === "en")?.imageUrl
              : null);
          if (uploadedImageUrl) {
            await updateAdminBlogPost({
              id: createdId,
              payload: { imageUrl: uploadedImageUrl },
            });
          }
        }
        notify({ type: "success", title: "Blog post created" });
      }
      closeEditor();
      await loadPosts();
    } catch (error) {
      notify({
        type: "error",
        title: "Save failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setSaving(false);
    }
  };

  const previewImageUrl = useMemo(() => resolveMediaUrl(form.imageUrl), [form.imageUrl]);

  const removePost = async (post) => {
    const ok = window.confirm(`Delete post #${post.id}?`);
    if (!ok) return;
    try {
      await deleteAdminBlogPost({ id: post.id });
      notify({ type: "success", title: "Blog post deleted" });
      await loadPosts();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Blog Manager</h2>
            <p className="text-sm text-slate-500 mt-1">Create, edit, publish, and localize blog posts.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={loadPosts}
              className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm"
            >
              New post
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-[10%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="w-[42%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Title (EN)</th>
              <th className="w-[20%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Published</th>
              <th className="w-[12%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="w-[16%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-4 text-sm text-slate-500" colSpan={5}>
                  Loading posts...
                </td>
              </tr>
            ) : sortedPosts.length ? (
              sortedPosts.map((post) => {
                const trEn = (post.translations || []).find((tr) => String(tr.language).toLowerCase() === "en");
                const titleEn = trEn?.title || post?.title || post?.Title || "-";
                return (
                  <tr key={post.id} className="border-b border-slate-200">
                    <td className="px-3 py-3 text-sm text-slate-700">{post.id}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{titleEn}</td>
                    <td className="px-3 py-3 text-sm text-slate-700">
                      {post.publishedAtUtc ? new Date(post.publishedAtUtc).toLocaleString() : "-"}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${post.isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                        {post.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(post)}
                          className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removePost(post)}
                          className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="px-3 py-4 text-sm text-slate-500" colSpan={5}>
                  No blog posts yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isEditorOpen ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-[#011337]">
              {editingPostId ? `Edit post #${editingPostId}` : "Create new post"}
            </h3>
            <button
              type="button"
              onClick={closeEditor}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Clear
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <label className="text-sm text-slate-700">
              Published at
              <input
                type="datetime-local"
                value={toInputDateTime(form.publishedAtUtc)}
                onChange={(e) => setForm((prev) => ({ ...prev, publishedAtUtc: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-7">
              <input
                type="checkbox"
                checked={Boolean(form.isActive)}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              Active
            </label>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4">
            <p className="text-sm font-semibold text-[#011337] mb-2">Post Image</p>
            {previewImageUrl ? (
              <img
                src={previewImageUrl}
                alt=""
                className="w-full max-w-xs h-28 object-cover rounded border border-slate-200 bg-white mb-2"
              />
            ) : (
              <p className="text-xs text-slate-500 mb-2">No image uploaded yet.</p>
            )}
            <div className="grid gap-2">
              <input type="file" onChange={(e) => setImageFile(e.target.files?.[0] || null)} className="text-xs" />
              <p className="text-xs text-slate-500">
                {imageFile
                  ? `Selected: ${imageFile.name} (will upload on save)`
                  : "Select one image file for this post."}
              </p>
              {editingPostId ? (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await deleteAdminBlogImage({ id: editingPostId, language: "en" });
                      await updateAdminBlogPost({
                        id: editingPostId,
                        payload: { imageUrl: null },
                      });
                      setForm((prev) => ({ ...prev, imageUrl: "" }));
                      notify({ type: "success", title: "Post image removed" });
                      await loadPosts();
                    } catch (error) {
                      notify({
                        type: "error",
                        title: "Remove image failed",
                        message: error?.response?.data?.message || error?.message || "",
                      });
                    }
                  }}
                  className="w-fit text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                >
                  Remove image
                </button>
              ) : null}
            </div>
          </div>

          <div className="space-y-4">
            {LANGS.map((lang) => {
              const tr = form.translations[lang] || emptyTranslation();
              return (
                <div key={lang} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-[#011337] mb-2">{lang.toUpperCase()} Translation</p>
                  <div className="grid gap-2">
                    <input
                      type="text"
                      value={tr.title}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          translations: {
                            ...prev.translations,
                            [lang]: { ...tr, title: e.target.value },
                          },
                        }))
                      }
                      placeholder="Title"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                    />
                    <textarea
                      value={tr.text}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          translations: {
                            ...prev.translations,
                            [lang]: { ...tr, text: e.target.value },
                          },
                        }))
                      }
                      placeholder="Text"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white min-h-[100px]"
                    />
                    <input
                      type="text"
                      value={tr.caption}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          translations: {
                            ...prev.translations,
                            [lang]: { ...tr, caption: e.target.value },
                          },
                        }))
                      }
                      placeholder="Caption"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                    />

                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-semibold text-[#011337] mb-1">Sections (paragraph / heading)</p>
                      <p className="text-xs text-slate-500 mb-2">
                        Same shape as the public API: <code className="text-[11px]">sectionType</code>,{" "}
                        <code className="text-[11px]">title</code> (headings), <code className="text-[11px]">body</code>,{" "}
                        <code className="text-[11px]">bullets</code> (one line per bullet).
                      </p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => addSection(lang, "paragraph")}
                          className="text-xs px-2 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50"
                        >
                          + Paragraph
                        </button>
                        <button
                          type="button"
                          onClick={() => addSection(lang, "heading")}
                          className="text-xs px-2 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50"
                        >
                          + Heading
                        </button>
                      </div>

                      {(tr.sections || []).length === 0 ? (
                        <p className="text-xs text-slate-400 italic mb-1">No sections — optional legacy body uses “Text” above.</p>
                      ) : null}

                      {(tr.sections || []).map((sec, si) => (
                        <div key={sec._key} className="rounded-lg border border-slate-200 bg-white p-2 mb-2 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <label className="text-xs text-slate-600 flex items-center gap-1">
                              Type
                              <select
                                value={sec.sectionType === "heading" ? "heading" : "paragraph"}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateSection(lang, si, {
                                    sectionType: v,
                                    ...(v === "heading" ? { bullets: [] } : {}),
                                  });
                                }}
                                className="rounded border border-slate-300 px-1.5 py-1 text-xs bg-white"
                              >
                                {SECTION_TYPES.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <span className="text-[11px] text-slate-400">#{si + 1}</span>
                            <div className="flex gap-1 ml-auto">
                              <button
                                type="button"
                                disabled={si === 0}
                                onClick={() => moveSection(lang, si, -1)}
                                className="text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                              >
                                Up
                              </button>
                              <button
                                type="button"
                                disabled={si >= (tr.sections || []).length - 1}
                                onClick={() => moveSection(lang, si, 1)}
                                className="text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                              >
                                Down
                              </button>
                              <button
                                type="button"
                                onClick={() => removeSection(lang, si)}
                                className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                              >
                                Remove
                              </button>
                            </div>
                          </div>

                          {sec.sectionType === "heading" ? (
                            <input
                              type="text"
                              value={sec.title}
                              onChange={(e) => updateSection(lang, si, { title: e.target.value })}
                              placeholder="Heading title (maps to API title)"
                              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            />
                          ) : null}

                          <textarea
                            value={sec.body}
                            onChange={(e) => updateSection(lang, si, { body: e.target.value })}
                            placeholder={
                              sec.sectionType === "heading"
                                ? "Optional subtitle (usually leave empty for headings)"
                                : "Paragraph body"
                            }
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[72px]"
                          />

                          {sec.sectionType === "paragraph" ? (
                            <textarea
                              value={(sec.bullets || []).join("\n")}
                              onChange={(e) => {
                                const bullets = e.target.value
                                  .split(/\r?\n/)
                                  .map((line) => line.trim())
                                  .filter(Boolean);
                                updateSection(lang, si, { bullets });
                              }}
                              placeholder="Bullets (optional) — one per line"
                              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[80px] font-mono text-xs"
                            />
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={closeEditor}
              className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={savePost}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm disabled:opacity-60"
            >
              {saving ? "Saving..." : editingPostId ? "Update post" : "Create post"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default BlogManager;
